import type { MarketId } from '../prices/decode';

/**
 * Listing venues, as codes.
 *
 * Two today, both Canadian, each written out in full on all 127 records —
 * "Toronto Stock Exchange (TSX)" is 28 characters stored 123 times.
 *
 * The market is here rather than derived from the venue name by regex, which is
 * what `calendar.ts` was doing. A calendar is chosen by this value, and a
 * regex that fails to match quietly defaults to Canada — which would put a US
 * listing on the wrong trading calendar and shift every date it implies.
 */
export interface ExchangeMeta {
    name: string;
    /** Which trading calendar its listings keep. */
    market: MarketId;
}

export const EXCHANGES: Record<string, ExchangeMeta> = {
    TSX: { name: 'Toronto Stock Exchange (TSX)', market: 'CA' },
    CBOE: { name: 'Cboe Canada', market: 'CA' },
    NYSE: { name: 'New York Stock Exchange', market: 'US' },
    NASDAQ: { name: 'Nasdaq', market: 'US' },
    ARCA: { name: 'NYSE Arca', market: 'US' },
};

export const exchangeOf = (code: string | null | undefined): ExchangeMeta | null => (code ? EXCHANGES[code] ?? null : null);
export const nameOfExchange = (code: string | null | undefined): string | null => exchangeOf(code)?.name ?? null;

const BY_NAME: Record<string, string> = Object.fromEntries(Object.entries(EXCHANGES).map(([code, meta]) => [meta.name.toLowerCase(), code]));

/** The code for a published venue name. Matches loosely, because an issuer may
    write "Cboe Canada" where another writes "Cboe Canada Inc." */
export const codeForExchange = (name: string | null | undefined): string | null => {
    if (!name) return null;
    const key = name.trim().toLowerCase();
    if (BY_NAME[key]) return BY_NAME[key];
    if (/cboe|neo\b/.test(key)) return 'CBOE';
    if (/toronto|tsx/.test(key)) return 'TSX';
    if (/nasdaq/.test(key)) return 'NASDAQ';
    if (/arca/.test(key)) return 'ARCA';
    if (/new york|nyse/.test(key)) return 'NYSE';
    return null;
};
